import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Check, Sparkles } from "lucide-react";

export default function Pricing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    company_name: ""
  });
  const [formKey, setFormKey] = useState(Date.now());

  // Initialize form - prevent autofill
  useEffect(() => {
    setFormData({
      email: "",
      password: "",
      company_name: ""
    });
    setFormKey(Date.now());
  }, []);

  const packages = [
    {
      id: "1_month",
      name: "1 Month Access",
      price: "$4",
      duration: "1 month",
      features: [
        "AI-powered job requisition generation",
        "Market salary analysis from 5+ sources",
        "Unlimited requisitions",
        "Export to PDF, Word, and Text",
        "Create Library of Requisitions ready for posting"
      ]
    },
    {
      id: "3_months",
      name: "3 Months Access",
      price: "$10",
      duration: "3 months",
      features: [
        "Everything in 1 Month plan",
        "Save 17% compared to monthly",
        "Continued access for long-term hiring",
        "Extended requisition library retention",
        "Best value for growing teams"
      ]
    },
    {
      id: "1_year",
      name: "1 Year Access",
      price: "$30",
      duration: "1 year",
      popular: true,
      features: [
        "Everything in 3 Months plan",
        "Save 38% compared to monthly",
        "Full year of unlimited access",
        "Early access to new features",
        "Best value for established teams"
      ]
    }
  ];

  const handleSubscribe = async (packageId) => {
    if (!formData.email || !formData.password || !formData.company_name) {
      toast.error("Please fill in all fields");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    setSelectedPackage(packageId);

    // Fire Google Ads conversion event for checkout initiation
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17902473018/nTcBCN2ej-wbELqeyNhC',
        'value': packageId === '1_month' ? 4 : packageId === '3_months' ? 10 : 30,
        'currency': 'USD'
      });
    }

    try {
      const originUrl = window.location.origin;
      
      const response = await apiClient.post("/subscriptions/create-checkout", {
        package_id: packageId,
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name,
        origin_url: originUrl
      });

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      
      // Handle different error formats
      let errorMessage = "Failed to create checkout session";
      
      if (error.response?.data?.detail) {
        // Check if detail is a string or object
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(", ");
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setLoading(false);
      setSelectedPackage("");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-xl font-bold text-slate-900">Create Your Account</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Choose Your Plan</h2>
          <p className="text-lg text-slate-600">Select a subscription plan to get started with SmartReqs</p>
        </div>

        {/* Image and Account Details Side by Side */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Left: Image */}
            <div className="flex items-center justify-center">
              <img 
                src="/choose-plan.png" 
                alt="Choose Your Plan" 
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
            
            {/* Right: Account Details Form */}
            <div>
              <Card className="h-full">
                <CardHeader>
                  <CardTitle>Account Details</CardTitle>
                  <CardDescription>Enter your information to continue</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4" key={formKey}>
                  <div className="space-y-2">
                    <Label htmlFor={`company_${formKey}`}>Company Name *</Label>
                    <Input
                      id={`company_${formKey}`}
                      name={`company_${formKey}`}
                      type="text"
                      placeholder="Acme Inc."
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      autoComplete="chrome-off"
                      data-lpignore="true"
                      data-form-type="other"
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`email_${formKey}`}>Email *</Label>
                    <Input
                      id={`email_${formKey}`}
                      name={`email_${formKey}`}
                      type="text"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      required
                      data-testid="email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`password_${formKey}`}>Password *</Label>
                    <Input
                      id={`password_${formKey}`}
                      name={`password_${formKey}`}
                      type="password"
                      placeholder="Min 6 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      required
                      minLength={6}
                      data-testid="password-input"
                    />
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-xs text-slate-500">
                      By subscribing, you agree to our Terms of Service and Privacy Policy
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <Card
                key={pkg.id}
                className={`relative card-hover ${pkg.popular ? 'ring-2 ring-blue-500' : ''}`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      BEST VALUE
                    </span>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-slate-900">{pkg.price}</span>
                    <span className="text-slate-600 ml-2">/ {pkg.duration}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Auto-renews • Cancel anytime</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Payment methods accepted */}
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500 text-center mb-2">Accepts</p>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded">💳 Credit/Debit Card</span>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => handleSubscribe(pkg.id)}
                    disabled={loading}
                    className="w-full mt-6"
                    variant={pkg.popular ? "default" : "outline"}
                    data-testid={`subscribe-${pkg.id}-btn`}
                  >
                    {loading && selectedPackage === pkg.id ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      `Subscribe Now`
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Legal Links Footer */}
        <div className="mt-12 text-center pb-8">
          <div className="flex items-center justify-center gap-6 text-sm">
            <button
              onClick={() => navigate("/terms-of-service")}
              className="text-slate-600 hover:text-blue-600 transition-colors underline"
            >
              Terms of Service
            </button>
            <span className="text-slate-400">•</span>
            <button
              onClick={() => navigate("/privacy-policy")}
              className="text-slate-600 hover:text-blue-600 transition-colors underline"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}